<template>
  <div class="home-page">
    <section class="welcome-panel">
      <div class="profile">
        <el-avatar :size="72" :src="avatarUrl">{{ avatarText }}</el-avatar>
        <div>
          <span class="eyebrow">Admin Console</span>
          <h2>欢迎回来，{{ username }}</h2>
          <p>{{ welcomeText }} 这里用于管理代理、用户积分、模型定价、风格素材和内容运营。</p>
        </div>
      </div>
      <div class="system-badge">
        <strong>AI Agent</strong>
        <span>运营管理后台</span>
      </div>
    </section>

    <section class="feature-grid">
      <article v-for="item in features" :key="item.title" class="feature-card">
        <div class="feature-icon">
          <el-icon>
            <component :is="item.icon" />
          </el-icon>
        </div>
        <div>
          <h3>{{ item.title }}</h3>
          <p>{{ item.desc }}</p>
        </div>
      </article>
    </section>

    <section class="guide-panel">
      <div class="guide-copy">
        <span class="eyebrow">Workflow</span>
        <h3>日常工作建议</h3>
        <p>先查看数据统计判断充值、消耗和分成趋势，再进入代理列表查看异常用户或代理，最后处理风格、模型、新闻和提现等运营配置。</p>
      </div>
      <div class="quick-links">
        <router-link v-for="link in quickLinks" :key="link.path" :to="link.path" class="quick-link">
          <span>{{ link.label }}</span>
          <el-icon>
            <ArrowRight />
          </el-icon>
        </router-link>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { ArrowRight, Coin, DataAnalysis, Files, Setting } from '@element-plus/icons-vue'

const store = useStore()

const username = computed(() => store.state.userInfo?.username || '管理员')
const avatarUrl = computed(() => store.state.userInfo?.avatar || '')
const avatarText = computed(() => String(username.value || '管').slice(0, 1))
const welcomeText = computed(() => new Date().getHours() < 12 ? '上午好，适合先看一眼今日数据。' : '辛苦了，继续把系统打理得清清爽爽。')

const features = [
  {
    title: '数据统计',
    desc: '汇总用户积分、充值收入、模型成本、代理分成和近期经营趋势。',
    icon: DataAnalysis
  },
  {
    title: '代理运营',
    desc: '查看代理调用、扣分、活跃用户、提现收益和用户消费记录。',
    icon: Coin
  },
  {
    title: '内容配置',
    desc: '维护风格、模型、发现页内容与公告素材，让前台体验保持统一。',
    icon: Files
  },
  {
    title: '系统设置',
    desc: '管理账号、品牌资料、模型价格和权限状态，减少人工排查成本。',
    icon: Setting
  }
]

const quickLinks = [
  { label: '数据统计', path: '/center' },
  { label: '代理详情', path: '/news-manage/newslist' },
  { label: '添加风格', path: '/addstyle' },
  { label: '发现管理', path: '/discover' }
]
</script>

<style scoped>
.home-page {
  min-height: 100%;
  box-sizing: border-box;
  padding: 24px;
  background: #060a12;
  color: #e8edf7;
}

:global(.el-main:has(.home-page)) {
  padding: 0;
  background: #060a12;
}

.welcome-panel,
.feature-card,
.guide-panel {
  border: 1px solid rgba(94, 116, 150, 0.22);
  border-radius: 8px;
  background: #0d1422;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.24);
}

.welcome-panel {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: center;
  padding: 26px;
  background:
    linear-gradient(135deg, rgba(255, 116, 23, 0.16), transparent 36%),
    linear-gradient(120deg, #101827 0%, #0b111d 66%, #111827 100%);
}

.profile {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 18px;
}

.profile h2 {
  margin: 6px 0 8px;
  color: #fff;
  font-size: 28px;
  line-height: 1.15;
}

.profile p,
.guide-copy p {
  max-width: 760px;
  margin: 0;
  color: #9fb0c7;
  line-height: 1.7;
}

.eyebrow {
  color: #ff9b4a;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.system-badge {
  flex-shrink: 0;
  min-width: 170px;
  padding: 18px;
  border: 1px solid rgba(255, 116, 23, 0.28);
  border-radius: 8px;
  background: rgba(255, 116, 23, 0.1);
}

.system-badge strong,
.system-badge span {
  display: block;
}

.system-badge strong {
  color: #fff;
  font-size: 20px;
}

.system-badge span {
  margin-top: 6px;
  color: #ffbd8a;
  font-size: 13px;
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin: 16px 0;
}

.feature-card {
  min-height: 148px;
  padding: 20px;
  box-sizing: border-box;
}

.feature-icon {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #67e8f9;
  background: rgba(14, 165, 233, 0.14);
}

.feature-card h3,
.guide-copy h3 {
  margin: 16px 0 8px;
  color: #fff;
  font-size: 18px;
}

.feature-card p {
  margin: 0;
  color: #9fb0c7;
  line-height: 1.65;
  font-size: 13px;
}

.guide-panel {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 24px;
  align-items: center;
  padding: 24px;
}

.guide-copy h3 {
  margin-top: 8px;
  font-size: 22px;
}

.quick-links {
  display: grid;
  gap: 10px;
}

.quick-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border: 1px solid rgba(94, 116, 150, 0.25);
  border-radius: 8px;
  color: #dbeafe;
  text-decoration: none;
  background: #101827;
  transition: border-color 0.18s ease, background 0.18s ease;
}

.quick-link:hover {
  border-color: rgba(255, 116, 23, 0.55);
  background: #151f30;
  color: #ffbd8a;
}

@media (max-width: 1200px) {
  .welcome-panel,
  .guide-panel {
    grid-template-columns: 1fr;
    flex-direction: column;
    align-items: stretch;
  }

  .feature-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
